import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const EmailSection = () => {
  const { language, t } = useLanguage();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState(''); // Hidden field for bot detection
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/subscribe`, {
        email: email.trim(),
        language: language,  // Send current language
        honeypot: honeypot // Will be empty for real users
      });

      if (response.data.success) {
        setIsSubmitted(true);
        setEmail('');
        toast.success(response.data.message);
      } else {
        // Duplicate email
        toast.info(response.data.message);
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please try again later.');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="signup"
      ref={sectionRef}
      className="relative py-12 md:py-20 overflow-hidden"
    >
      {/* Transparent - stars show through */}

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6">
        <div className={`transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          {/* Card - dark theme with warm accent */}
          <div className="rounded-3xl border border-amber-200/15 bg-[#f5ebe0]/[0.06] p-6 backdrop-blur-sm shadow-2xl shadow-black/20 md:p-10">
            <div className="mx-auto max-w-2xl">
              {/* Content */}
              <h2 className="font-sans text-2xl md:text-3xl text-[#f5ebe0]/90 text-center mb-3">
                {t(translations.email.headline)}
              </h2>
              {/* Description - EB Garamond */}
              <p className="text-amber-200/60 font-serif text-base md:text-lg text-center mb-6 md:mb-8">
                {t(translations.email.description)}
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot field - hidden from users, visible to bots */}
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    opacity: 0,
                    height: 0,
                    width: 0
                  }}
                  aria-hidden="true"
                />

                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder={t(translations.email.placeholder)}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting || isSubmitted}
                    className="flex-grow bg-[#2a2a45] border border-amber-200/20 focus:border-amber-200/40 rounded-xl py-6 px-4 font-sans text-[#f5ebe0] placeholder:text-[#f5ebe0]/40"
                  />
                  <Button
                    type="submit"
                    disabled={isSubmitting || isSubmitted || !email}
                    className="bg-[#b8956b] hover:bg-[#a8855b] text-white px-8 py-6 font-sans rounded-xl shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t(translations.email.sending)}
                      </span>
                    ) : isSubmitted ? (
                      <span className="flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        {t(translations.email.subscribed)}
                      </span>
                    ) : (
                      <span className="font-sans">
                        {t(translations.email.buttonText)}
                      </span>
                    )}
                  </Button>
                </div>
              </form>

              {/* Privacy note */}
              <p className="text-[#f5ebe0]/40 text-sm text-center mt-6 font-serif">
                {t(translations.email.privacyNote)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmailSection;
